/**
 * src/content/narrative.ts — rule-based results narrative + contextual warnings
 * (§10.8, Appendix C). Pure templating over computed values — NEVER fabricated numbers.
 */
import type { ProjectionResult, ScenarioInputs } from '@/types';
import { fmtCurrency, fmtPct, fmtRatioPct } from '@/lib/format';

const FREQ_LABEL: Record<ScenarioInputs['drawFrequency'], string> = {
  monthly: 'month',
  quarterly: 'quarter',
  annual: 'year',
  one_time: 'one-time draw',
};

export interface NarrativeOutput {
  paragraphs: string[];
  warnings: string[];
}

export function generateNarrative(result: ProjectionResult, inputs: ScenarioInputs): NarrativeOutput {
  const { summary, estate, rows } = result;
  const paragraphs: string[] = [];
  const warnings: string[] = [];

  // Opening.
  paragraphs.push(
    `You're borrowing ${fmtCurrency(inputs.drawAmount)}/${FREQ_LABEL[inputs.drawFrequency]} against a ${fmtCurrency(inputs.portfolioValue)} portfolio at a starting rate of ${fmtPct(summary.startApr)}.`,
  );

  // Safety.
  const cushion = fmtRatioPct(summary.minCallCushionPct);
  if (summary.minCallCushionPct >= 0.4) {
    paragraphs.push(`Your loan stays comfortably within safe limits — even a ${cushion} drop wouldn't trigger a maintenance call.`);
  } else if (summary.minCallCushionPct >= 0.15) {
    paragraphs.push(`Your cushion gets somewhat tight (as low as ${cushion}); a sharp downturn could force action.`);
  } else {
    paragraphs.push(`⚠️ Your cushion is thin (${cushion}); a moderate market drop could trigger a maintenance call.`);
  }

  // Wealth.
  if (summary.carryIsAccretive) {
    paragraphs.push(
      `Because your assumed ${fmtPct(inputs.portfolioExpectedReturnAnnualPct)} return exceeds your after-tax loan cost of ${fmtPct(summary.afterTaxApr)}, your net worth still grows, ending near ${fmtCurrency(summary.endingNetWorth)}.`,
    );
  } else {
    paragraphs.push(
      `⚠️ Your assumed return (${fmtPct(inputs.portfolioExpectedReturnAnnualPct)}) is below your after-tax loan cost (${fmtPct(summary.afterTaxApr)}), so carrying this loan erodes net worth over time.`,
    );
  }

  // Borrow vs sell / estate.
  if (estate.difference > 0) {
    paragraphs.push(
      `Versus selling assets to fund the same spending, borrowing leaves about ${fmtCurrency(estate.difference)} more to heirs, largely because a step-up in basis can avoid roughly ${fmtCurrency(estate.lifetimeIncomeTaxAvoided)} of income tax — but this assumes current step-up law and that you avoid forced sales in a downturn.`,
    );
  } else {
    paragraphs.push(
      "In this scenario, selling would leave heirs as well off or better — borrowing's tax advantage doesn't outweigh its cost here.",
    );
  }

  // Warnings.
  if (inputs.paymentMode === 'capitalize' && !summary.carryIsAccretive) {
    warnings.push('You are letting interest compound while your return trails the loan cost — the balance snowballs and net worth erodes.');
  }
  if (summary.minCallCushionPct < 0.15) {
    warnings.push('Your maintenance-call cushion is thin; a moderate market drop could force taxable sales.');
  }
  if (rows.some((r) => r.maintenanceCall || r.forcedSale > 0)) {
    warnings.push('At least one month triggers a maintenance call and forced liquidation in this scenario — the crash risk buy-borrow-die is meant to avoid.');
  }
  // Sell gross-up denominator issue (§6.14).
  const startBasis = inputs.costBasis !== null ? inputs.costBasis : (inputs.portfolioValue * inputs.costBasisPct) / 100;
  const gain = inputs.portfolioValue > 0 ? Math.max(0, 1 - startBasis / inputs.portfolioValue) : 0;
  const combined = (inputs.tax.longTermCapGainsRatePct + inputs.tax.stateCapGainsRatePct) / 100;
  if (1 - gain * combined <= 0) {
    warnings.push('Your gain fraction × tax rate is so high that the sell-path gross-up is unbounded; the borrow-vs-sell comparison is capped and approximate.');
  }
  // Estate/step-up caveat whenever the estate view is meaningful.
  warnings.push('Estate and step-up-in-basis figures assume current U.S. law, which may change. Confirm with a qualified professional.');

  return { paragraphs, warnings };
}
